<style>
:root {
  --image-button-color: var(--primary-blue-400, red);
  --image-button-background: var(--background, white);
}

.image-input {
  display: inline-block;
  max-width: 100%;
  position: relative;
}

.image-input__preview {
  display: block;
  height: auto;
  max-width: 100%;
  width: 100%;
}

.image-input__preview:not([data-disabled]) {
  cursor: -webkit-grab;
}

.image-input__preview:not([data-disabled]):active {
  cursor: -webkit-grabbing;
}

.image-input__label {
  border: 1px solid #cccccc;
  border-radius: 5px;
  display: block;
  max-width: 100%;
}

.image-input__input {
  left: 0;
  position: fixed;
  top: 100%;
}
input.image-input__input {
  display: none !important;
}

.image-input__slider {
  width: 200px;
}

@media only screen and (max-width: 768px) {
  .image-input__slider {
    width: 180px;
  }
}

.image-input__content {
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.image-input__error {
  color: var(--highlight);
  font-size: 0.8em;
  font-weight: bold;
  margin-bottom: 0.5em;
  text-align: center;
}

.icon-image {
  color: var(--image-button-color);
  border: 0;
  font-size: 1.7em;
}

.icon-image span.cp-icon__container {
  position: initial !important;
}

div.image-input.comment div label div.image-input__height-provider {
  padding-top: 0 !important;
}

.image-input__content-container {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.imageInputMinHeight {
  min-height: 200px;
}

div.image-input.comment div label div.image-input__content-container span.icon-image {
  color: gray;
  cursor: pointer;
}
</style>

<template>
  <div class="image-input" :style="value.image && { width: isRectangularImage ? '100%' : '70%' }">
    <div v-if="value.image" :style="[icon ? { width: `100%`, paddingTop: `20px` } : { width: 'auto' }]">
      <svg
        class="image-input__preview"
        :viewBox="`${-1 * currentMargin} ${-1 * currentMargin} ${localWidth + currentMargin * 2} ${localHeight + currentMargin * 2}`"
        :width="localWidth"
        :height="localHeight"
        :data-disabled="disabled"
        @mousedown="handleStart"
        @touchstart="handleStart"
      >
        <g :transform="`scale(${currentScale}) translate(${-1 * currentCoords.x}, ${-1 * currentCoords.y})`">
          <image :xlink:href="value.image" :width="this.naturalWidth" :height="this.naturalHeight" />
        </g>
        <path
          v-if="circular"
          :d="`
                    M ${-3 * currentMargin} ${-3 * currentMargin}
                    L ${localWidth + currentMargin * 3} ${-3 * currentMargin}
                    L ${localWidth + currentMargin * 3} ${localHeight + currentMargin * 3}
                    L ${-3 * currentMargin} ${localHeight + currentMargin * 3}
                    Z
                    M ${localWidth / 2} 0
                    A ${localWidth / 2} ${localHeight / 2} 0 0 0 0 ${localHeight / 2}
                    A ${localWidth / 2} ${localHeight / 2} 0 0 0 ${localWidth / 2} ${localHeight}
                    A ${localWidth / 2} ${localHeight / 2} 0 0 0 ${localWidth} ${localHeight / 2}
                    A ${localWidth / 2} ${localHeight / 2} 0 0 0 ${localWidth / 2} 0
                    Z
                `"
          :fill="mask"
          stroke="none"
        />

        <path
          v-else
          :d="`
                    M ${-3 * currentMargin} ${-3 * currentMargin}
                    L ${localWidth + currentMargin * 3} ${-3 * currentMargin}
                    L ${localWidth + currentMargin * 3} ${localHeight + currentMargin * 3}
                    L ${-3 * currentMargin} ${localHeight + currentMargin * 3}
                    Z
                    M 0 0
                    L 0 ${localHeight}
                    L ${localWidth} ${localHeight}
                    L ${localWidth} 0
                    Z
                `"
          :fill="mask"
          stroke="none"
        />
      </svg>
    </div>

    <div v-else>
      <label>
        <div :class="{ 'image-input__content-container': true, imageInputMinHeight: !icon }">
          <div v-if="error" class="image-input__error">{{ error }}</div>
          <input
            type="file"
            v-if="!resettingFileInput"
            class="image-input__input"
            accept="image/jpeg,image/png"
            :disabled="disabled"
            @change="handleFileChange"
          />
          <slot>
            <span v-if="icon" class="icon-image">
              <cp-icon name="camera"></cp-icon>
            </span>
            <span v-else class="content-2" style="color: #555555"> Click to <span style="color: #ed572f">upload</span> from your files </span>
          </slot>
        </div>
      </label>
    </div>

    <cp-grid v-if="value.image && !disabled" tight align="center" justify="spaceBetween" v-margin-top="1" :style="[icon ? { marginBottom: '20px' } : '']">
      <cp-grid-item>
        <input
          type="range"
          min="1"
          max="2"
          step="0.01"
          :value="scaleInputValue"
          :title="scaleInputValue.toFixed(2)"
          @input="handleScaleChange"
          class="image-input__slider"
        />
      </cp-grid-item>
      <cp-grid-item>
        <cp-button @click="clearImage">{{ clearLabel }}</cp-button>
      </cp-grid-item>
    </cp-grid>
  </div>
</template>

<script>
import imageFileToBase64 from 'image-file-to-base64-exif';

function getImageDataURL(file) {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

function getImg(url) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.src = url;
  });
}

export default {
  directives: {
    margin: require('../directives/margins').margin,
    marginBottom: require('../directives/margins').marginBottom,
    marginTop: require('../directives/margins').marginTop,
  },

  components: {
    CpButton: require('../button').default,
    CpButtonBase: require('../button-base').default,
    CpGrid: require('../grid').default,
    CpGridItem: require('../grid-item').default,
    CpIcon: require('../icon').default,
  },

  props: {
    value: {
      type: Object,
      default() {
        return this.makeEmptyValue();
      },
    },
    width: { type: Number, default: 100 },
    height: { type: Number, default: 100 },
    margin: { type: Number, default: 20 },
    circular: { type: Boolean, default: false }, // For display only. The crop is still stored as the bounding rectangle because the image will be made round with CSS.
    mask: { type: String, default: 'rgba(0, 0, 0, 0.5)' },
    clearLabel: { type: String, default: 'Remove' },
    disabled: Boolean,
    icon: { type: Boolean, default: false },
  },

  data() {
    return {
      localWidth: this.width,
      localHeight: this.height,
      error: null,
      resettingFileInput: false,
      naturalWidth: 0,
      naturalHeight: 0,
      dragState: null,
      maxlength: 25000000,
    };
  },

  computed: {
    imageIsTallerThanInput() {
      const imageAspectRatio = this.naturalWidth / this.naturalHeight;
      const inputAspectRatio = this.localWidth / this.localHeight;
      return imageAspectRatio < inputAspectRatio;
    },

    currentScale() {
      if (this.imageIsTallerThanInput) {
        return this.localWidth / this.currentCoords.width;
      } else {
        return this.localHeight / this.currentCoords.height;
      }
    },

    scaleInputValue() {
      if (this.imageIsTallerThanInput) {
        return this.naturalWidth / this.currentCoords.width;
      } else {
        return this.naturalHeight / this.currentCoords.height;
      }
    },

    currentCoords() {
      // Provide sensible centered coords if none are set.
      let { x, y, width, height } = this.value;
      if (!x && x !== 0) x = this.imageIsTallerThanInput ? 0 : (this.naturalWidth - (this.localWidth / this.localHeight) * this.naturalHeight) / 2;
      if (!y && y !== 0) y = this.imageIsTallerThanInput ? (this.naturalHeight - (this.localHeight / this.localWidth) * this.naturalWidth) / 2 : 0;
      if (!width && width !== 0) width = this.imageIsTallerThanInput ? this.naturalWidth : (this.localWidth / this.localHeight) * this.naturalHeight;
      if (!height && height !== 0) height = this.imageIsTallerThanInput ? (this.localHeight / this.localWidth) * this.naturalWidth : this.naturalHeight;
      return { x, y, width, height };
    },

    currentMargin() {
      return this.disabled ? 0 : this.margin;
    },

    isRectangularImage() {
      return this.naturalWidth > this.naturalHeight;
    },
  },

  async mounted() {
    this.storeNaturalDimensionsFor(this.value.image);
  },

  methods: {
    makeEmptyValue() {
      return { image: null, x: null, y: null, width: null, height: null };
    },

    async storeNaturalDimensionsFor(src) {
      if (src) {
        const img = await getImg(src);
        this.localWidth = img.naturalWidth;
        this.localHeight = img.naturalHeight;
        this.naturalWidth = img.naturalWidth;
        this.naturalHeight = img.naturalHeight;
      } else {
        this.naturalWidth = 0;
        this.naturalHeight = 0;
      }
    },

    async handleFileChange(event) {
      try {
        this.error = null;
        const file = event.target.files[0];
        if (file.size > this.maxlength) {
          this.error = 'Sorry, the file exceeds the maximum allowed upload size.';
        } else {
          const urlGetter = file.type === 'image/jpeg' ? imageFileToBase64 : getImageDataURL;
          const dataURL = await urlGetter(file);
          await this.storeNaturalDimensionsFor(dataURL);
          const newValue = Object.assign({ image: dataURL }, this.currentCoords);
          this.emitChange(newValue);
        }
      } catch (error) {
        this.error = 'Sorry, there was an error reading that file.';
      } finally {
        this.resetFileInput();
      }
    },

    resetFileInput() {
      // Destroy and recreate the file input between selections to clear its value.
      this.resettingFileInput = true;
      setTimeout(() => {
        this.resettingFileInput = false;
      });
    },

    handleStart(event) {
      if (this.disabled) {
        return;
      }

      event.preventDefault();

      const isTouch = event.type.indexOf('touch') === 0;
      const pointer = isTouch ? event.touches[0] : event;
      this.dragState = {
        isTouch,
        startPageX: pointer.pageX,
        startPageY: pointer.pageY,
        startValueX: this.value.x,
        startValueY: this.value.y,
      };

      addEventListener(isTouch ? 'touchmove' : 'mousemove', this.handleMove);
      addEventListener(isTouch ? 'touchend' : 'mouseup', this.handleRelease);
      addEventListener('touchcancel', this.handleRelease);
    },

    handleMove(event) {
      const pointer = this.dragState.isTouch ? event.touches[0] : event;
      const dx = (pointer.pageX - this.dragState.startPageX) / this.currentScale;
      const dy = (pointer.pageY - this.dragState.startPageY) / this.currentScale;

      this.emitChange({
        x: Math.min(Math.max(this.dragState.startValueX - dx, 0), this.naturalWidth - this.value.width),
        y: Math.min(Math.max(this.dragState.startValueY - dy, 0), this.naturalHeight - this.value.height),
      });
    },

    handleRelease(event) {
      removeEventListener(this.dragState.isTouch ? 'touchmove' : 'mousemove', this.handleMove);
      removeEventListener(this.dragState.isTouch ? 'touchend' : 'mouseup', this.handleRelease);
      removeEventListener('touchcancel', this.handleRelease);
      this.dragState = null;
    },

    handleScaleChange(event) {
      let change, width, height;
      if (this.imageIsTallerThanInput) {
        change = this.naturalWidth / parseFloat(event.target.value) - this.currentCoords.width;
        width = this.currentCoords.width + change;
        height = this.localHeight / this.currentScale;
      } else {
        change = this.naturalHeight / parseFloat(event.target.value) - this.currentCoords.height;
        width = this.localWidth / this.currentScale;
        height = this.currentCoords.height + change;
      }
      const x = Math.min(Math.max(this.currentCoords.x - change / 2, 0), this.naturalWidth - width);
      const y = Math.min(Math.max(this.currentCoords.y - change / 2, 0), this.naturalHeight - height);
      this.emitChange({ x, y, width, height });
    },

    clearImage() {
      const emptyValue = this.makeEmptyValue();
      this.emitChange(emptyValue);
    },

    emitChange(changes) {
      const value = Object.assign({}, this.value, changes);
      this.$emit('input', value);
    },
  },

  watch: {
    'value.image': function (image) {
      this.storeNaturalDimensionsFor(image);
    },
  },
};
</script>
